import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useToast } from "@/hooks/use-toast";
import { queryClient } from "@/lib/queryClient";
import { authenticatedFetch } from "@/lib/authenticatedFetch";
import { Link } from "wouter";

interface TermsAcceptanceModalProps {
  open: boolean;
  onAccept: () => void;
}

export function TermsAcceptanceModal({ open, onAccept }: TermsAcceptanceModalProps) {
  const [agreed, setAgreed] = useState(false);
  const { toast } = useToast();

  const acceptTermsMutation = useMutation({
    mutationFn: async () => {
      const response = await authenticatedFetch('/api/auth/accept-terms', {
        method: 'POST',
      });

      if (!response.ok) {
        throw new Error('Failed to accept terms');
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/auth/user'] });
      onAccept();
      toast({
        title: "Welcome!",
        description: "You've successfully accepted the terms and conditions.",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to accept terms. Please try again.",
        variant: "destructive",
      });
    },
  });

  const handleAccept = () => {
    if (!agreed) {
      toast({
        title: "Please accept terms",
        description: "You must agree to the terms and conditions to continue.",
        variant: "destructive",
      });
      return;
    }
    acceptTermsMutation.mutate();
  };

  return (
    <Dialog open={open} onOpenChange={() => { }}>
      <DialogContent className="max-w-3xl max-h-[90vh]" data-testid="dialog-terms-acceptance">
        <DialogHeader>
          <DialogTitle className="text-2xl">Terms and Conditions</DialogTitle>
          <DialogDescription>
            Please read and accept our terms and conditions to continue using Waiterix.
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="h-[400px] pr-4">
          <div className="prose prose-sm dark:prose-invert max-w-none">
            <h3 className="font-bold mb-2 text-primary">1. Acceptance of Terms</h3>
            <p className="mb-4 text-xs">
              By using Waiterix, you agree to these Terms of Service. If you do not agree, please do not use the platform.
            </p>

            <h3 className="font-bold mb-2 text-primary">2. Service Description</h3>
            <p className="mb-4 text-xs">
              Waiterix is an AI-powered hospitality platform that automates front-of-house operations through AI workers, including the AI Waiter, AI Receptionist, and AI Business Manager. Features may include ordering assistance, reservations, digital menus, analytics, and customer interaction tools.
            </p>

            <h3 className="font-bold mb-2 text-primary">3. Subscription & Refund Policy</h3>
            <p className="mb-4 text-xs">
              Waiterix starts at <strong>$149 per month</strong>, plus AI usage costs. New customers are eligible for a 30-day money-back guarantee if they are not satisfied with the service.
            </p>

            <h3 className="font-bold mb-2 text-primary">4. AI Usage</h3>
            <p className="mb-4 text-xs">
              Waiterix uses artificial intelligence and third-party AI providers to power its services. AI responses may occasionally be inaccurate, and businesses are responsible for monitoring customer interactions when appropriate.
            </p>

            <h3 className="font-bold mb-2 text-primary">5. User Content</h3>
            <p className="mb-4 text-xs">
              You retain ownership of all content you upload, including menus, branding, and operational data. By using Waiterix, you grant us permission to store and process this information to provide and improve the service.
            </p>

            <h3 className="font-bold mb-2 text-primary">6. Account Termination</h3>
            <p className="mb-4 text-xs">
              You may delete your account at any time. Upon deletion, access to the platform will end and associated data may be permanently removed.
            </p>

            <h3 className="font-bold mb-2 text-primary">7. Limitation of Liability</h3>
            <p className="mb-4 text-xs">
              Waiterix is provided “as is.” We are not liable for service interruptions, AI errors, data loss, or failures of third-party services.
            </p>

            <h3 className="font-bold mb-2 text-primary">8. Communications & Updates</h3>
            <p className="mb-4 text-xs">
              I agree to be contacted by Waiterix via phone call, email, and SMS/text message regarding products, services, and updates. I understand I can opt out at any time.
            </p>

            <p className="text-sm border-t pt-4 font-medium">
              For full legal details, please review our complete{" "}
              <Link href="/terms" className="text-primary hover:underline font-bold" data-testid="link-full-terms">
                Terms and Conditions
              </Link>{" "}
              on the Waiterix website.
            </p>
          </div>
        </ScrollArea>

        <div className="flex items-center space-x-2 mt-4">
          <Checkbox
            id="terms"
            checked={agreed}
            onCheckedChange={(checked) => setAgreed(checked as boolean)}
            data-testid="checkbox-agree-terms"
          />
          <label
            htmlFor="terms"
            className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
          >
            I have read and agree to the Terms and Conditions
          </label>
        </div>

        <DialogFooter>
          <Button
            onClick={handleAccept}
            disabled={!agreed || acceptTermsMutation.isPending}
            className="w-full"
            data-testid="button-accept-terms"
          >
            {acceptTermsMutation.isPending ? "Accepting..." : "Accept and Continue"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
